import { Injectable } from '@angular/core';
import { Amplify } from 'aws-amplify';
import { post } from 'aws-amplify/api';
import awsmobile from 'src/aws-exports';
import { promiseTimeout, removeOpeningEllipsis } from '../tts-utils';
import {
  CLIENT_WORD_METADATA_DB,
  phonemeLookup,
  spanishPhonemeLookup,
} from '../../types/phoneme-types';
import { ConfigurationService } from 'src/app/modules/configuration/services/configuration/configuration.service';

export const TTS_ENGINE_TYPE = {
  standard: 'standard',
  generative: 'generative',
};

export type Utterance = {
  speech_marks: object[];
  speech_audio: number[];
  audioURL: string;
  id: number;
  text: string;
};

export type GetTextToSpeechArgs = {
  textToSpeak: string;
  includeSpeechMarks: boolean;
  locale: string;
  engine: string;
};

export type PhonemeProps = {
  phoneme: string;
  friendlyName: string;
  url: string;
};

/**
 * Service to interface with the Amira text-to-speech API.
 */
@Injectable({ providedIn: 'root' })
export class AmiraTTSService {
  private utterance_store: { [key: string]: Utterance } = {};

  constructor(private configService: ConfigurationService) {
    Amplify.configure(awsmobile);
  }

  /**
   * Get and cache speech audio for the specified text.
   * @param text text to speak
   * @param includeSpeechMarks `true` to get speech marks for lip sync
   * @param locale locale to use
   * @param timeout maximum time in ms to wait for a response before failure
   * @param useGenerative `true` to use the generative text-to-speech engine
   * @returns object containing `audioURL` for the generated speech audio
   */
  async getUtterance(
    text: string,
    includeSpeechMarks = false,
    locale = 'en-us',
    timeout = 10000,
    useGenerative = false
  ): Promise<Utterance> {
    if (!text) {
      return Promise.reject(new Error('No utterance provided.'));
    }

    const key = this.getUtteranceStoreKey(text, includeSpeechMarks, locale);
    if (Object.prototype.hasOwnProperty.call(this.utterance_store, key)) {
      // If we've already stored this utterance, pull it from the cache
      return new Promise(resolve => {
        const returnValue = structuredClone(this.utterance_store[key]);
        returnValue.id = new Date().getTime();
        resolve(returnValue);
      });
    } else {
      const args: GetTextToSpeechArgs = {
        textToSpeak: '<speak>' + text?.replaceAll('\\"', '"') + '</speak>', // replace escaped quotes as TTS service adds random sounds when passed with escaped quotes
        includeSpeechMarks,
        locale,
        engine:
          useGenerative && locale.toLowerCase() === 'en-us'
            ? TTS_ENGINE_TYPE.generative
            : TTS_ENGINE_TYPE.standard,
      };

      return promiseTimeout(this.getTextToSpeech(args), timeout)
        .then(async resp => {
          // Build the audio buffer
          const uInt8Array = new Uint8Array(resp.speech_audio.AudioStream.data);
          const arrayBuffer = uInt8Array.buffer;
          const blob = new Blob([arrayBuffer], { type: 'audio/mpeg' });
          const audioURL = URL.createObjectURL(blob);

          const utterance: Utterance = {
            speech_marks: resp.speech_marks,
            speech_audio: resp.speech_audio.AudioStream.data,
            audioURL,
            id: new Date().getTime(),
            text: args.textToSpeak,
          };

          // Cache the utterance
          this.utterance_store[key] = utterance;

          return Promise.resolve(structuredClone(utterance));
        })
        .catch(err => Promise.reject(err));
    }
  }

  /**
   * Get an utterance store key for the specified data.
   * @param text utterance text
   * @param includeSpeechMarks `true` if the utterance data includes speech marks for lip sync
   * @param locale locale used for the utterance
   * @returns key string
   */
  getUtteranceStoreKey(
    text: string,
    includeSpeechMarks: boolean,
    locale: string
  ): string {
    return text.replace(/\s/g, '') + '_' + includeSpeechMarks + '_' + locale;
  }

  /**
   * Get audio data from the Amira text-to-speech API for the specified arguments.
   * @param args input arguments
   * @param retries maximum number of retries to attempt
   * @returns JSON response from the API
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getTextToSpeech(args: GetTextToSpeechArgs, retries = 4): Promise<any> {
    const locale = args.locale;
    const engine = args.engine;

    args.textToSpeak = args.textToSpeak.replace(/[“”]/g, '"');
    if (engine === TTS_ENGINE_TYPE.generative) {
      // Generative voice does not support opening ellipsis
      args.textToSpeak = removeOpeningEllipsis(args.textToSpeak);
    }

    // Get a transcription
    const apiName = 'lexaSpeech';
    const path = '/TextToSpeech';
    const myInit = {
      headers: { 'Content-Type': 'application/json' },
      body: { inText: args.textToSpeak, locale, engine },
    };

    return (
      post({ apiName, path, options: myInit })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .response.then((resp: any) => resp?.body?.json())
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((json: any) => {
          if (json) {
            return Promise.resolve(json);
          } else {
            throw new Error('No response body.');
          }
        })
        .catch((err: Error) => {
          if (retries) {
            console.log('RETRYING TTS');
            return this.getTextToSpeech(args, retries - 1);
          } else {
            return Promise.reject(err);
          }
        })
    );
  }

  private phonemeToURL(phon: string, isWordPart = false): string {
    return `${this.configService.config.amiraAssetDomain}/phonemes/${
      isWordPart ? 'word-parts/' : ''
    }${phon}.mp3`;
  }

  getPhonemeSounds(
    phonemeStrings: string[] | string,
    locale = 'en-us'
  ): PhonemeProps[] {
    const lookupTable =
      locale === 'en-us' ? phonemeLookup : spanishPhonemeLookup;
    const outputPhonemes: PhonemeProps[] = [];
    let outIndex = 0;

    for (let i = 0; i < phonemeStrings.length; i++) {
      // Filter entries that match the first character
      const matches = lookupTable.filter(
        row => row.phoneme.indexOf(phonemeStrings[i]) === 0
      );

      if (matches.length) {
        if (matches.length === 1) {
          if (matches[0].phoneme.length === 1) {
            // Single char match
            outputPhonemes[outIndex] = {
              phoneme: matches[0].phoneme,
              friendlyName: matches[0].filename,
              url: this.phonemeToURL(matches[0].filename),
            };
            outIndex++;
          } else if (
            matches[0].phoneme.length > 1 &&
            matches[0].phoneme[1] === phonemeStrings[i + 1]
          ) {
            // 2 char match
            outputPhonemes[outIndex] = {
              phoneme: matches[0].phoneme,
              friendlyName: matches[0].filename,
              url: this.phonemeToURL(matches[0].filename),
            };
            outIndex++;
            i++; // Increment because we matched two chars
          } else {
            console.warn('Possible typo in phonemeString:', phonemeStrings);
          }
        } else {
          // Multiple matches, narrow it down
          const combinedMatches = matches.filter(
            row => row.phoneme.indexOf(phonemeStrings[i + 1]) === 1
          );
          if (combinedMatches.length) {
            outputPhonemes[outIndex] = {
              phoneme: combinedMatches[0].phoneme,
              friendlyName: combinedMatches[0].filename,
              url: this.phonemeToURL(combinedMatches[0].filename),
            };
            outIndex++;
            i++; // Increment index because we matched two chars
          } else {
            // Choose single char match if no double char matches
            const singleCharMatch = matches.find(
              ({ phoneme }) => phoneme.length === 1
            );
            if (singleCharMatch?.filename) {
              outputPhonemes[outIndex] = {
                phoneme: singleCharMatch.phoneme,
                friendlyName: singleCharMatch.filename,
                url: this.phonemeToURL(singleCharMatch.filename),
              };
              outIndex++;
            }
          }
        }
      }
    }

    return outputPhonemes;
  }

  getPhonemeSoundsForWordParts(wordParts: string[]): PhonemeProps[] {
    const outputPhonemes: PhonemeProps[] = [];
    let counter = 0;
    for (const part of wordParts) {
      const wordPartMetadata =
        CLIENT_WORD_METADATA_DB.WORDPART[part.toLowerCase()];
      if (wordPartMetadata) {
        outputPhonemes[counter] = {
          phoneme: wordPartMetadata.phoneme,
          friendlyName: wordPartMetadata.filename,
          url: this.phonemeToURL(wordPartMetadata.filename, true),
        };
      }
      counter++;
    }

    return outputPhonemes;
  }
}
